import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { useRef, useState, useCallback, useEffect } from 'react';
import '../../utils/assertion.esm.js';
import '../../utils/ssr.esm.js';
import '../../utils/number.esm.js';
import '../../utils/array.esm.js';
import { px } from '../../utils/breakpoint.esm.js';
import '../../utils/context.esm.js';
import { contains, getRelatedTarget } from '../../utils/dom.esm.js';
import '../../utils/tabbable.esm.js';
import '../../utils/dom-query.esm.js';
import '../../utils/logger.esm.js';
import '../../utils/focus.esm.js';
import { callAllHandlers } from '../../utils/function.esm.js';
import '../../utils/keydown.esm.js';
import { determineLazyBehavior } from '../../utils/lazy.esm.js';
import '../../utils/object.esm.js';
import { mergeRefs } from '../../utils/react-helpers.esm.js';
import '../../utils/style-props.esm.js';
import '../../utils/tailwind.esm.js';
import '../../hooks/use-safe-layout-effects.esm.js';
import '../../hooks/use-callback-ref.esm.js';
import '../../hooks/use-clickable.esm.js';
import '../../hooks/use-clipboard.esm.js';
import '../../hooks/use-combobox-dropdown-style.esm.js';
import '../../hooks/use-combobox-style.esm.js';
import '../../hooks/use-controllable.esm.js';
import '../../hooks/use-local-storage.esm.js';
import '../../hooks/use-media.esm.js';
import '../../hooks/use-prefers-dark-mode.esm.js';
import '../../hooks/use-dark-mode.esm.js';
import '../../hooks/use-force-update.esm.js';
import '../../hooks/use-descendant.esm.js';
import { useIds } from '../../hooks/use-id.esm.js';
import '../../hooks/use-previous.esm.js';
import { useDisclosure } from '../../hooks/use-disclosure.esm.js';
import '../../hooks/use-dropdown-style.esm.js';
import '../../hooks/use-latest-ref.esm.js';
import '../../hooks/use-event-listener.esm.js';
import '../../hooks/use-event-listener-v2.esm.js';
import '../../hooks/use-update-effect.esm.js';
import { useFocusOnHide } from '../../hooks/use-focus-on-hide.esm.js';
import '../../hooks/use-pointer-event.esm.js';
import { useFocusOnPointerDown } from '../../hooks/use-focus-on-pointerdown.esm.js';
import { useFocusOnShow } from '../../hooks/use-focus-on-show.esm.js';
import '../../hooks/use-fork-ref.esm.js';
import '../../hooks/use-hints.esm.js';
import '../../hooks/use-input-style.esm.js';
import '../../hooks/use-key-press.esm.js';
import '../../hooks/use-lock-body-scroll.esm.js';
import '../../hooks/use-platform.esm.js';
import '../../hooks/use-shortcut.esm.js';
import { cssVars } from '../Popper/popper.utils.esm.js';
import '../Popper/popper.modifiers.esm.js';
import '../Popper/use-popper.esm.js';
import { usePopperV2 } from '../Popper/use-popper-v2.esm.js';

var TRIGGER = {
  click: 'click',
  hover: 'hover'
};
/**
 * @internal
 */

function usePopover(usePopoverProps) {
  if (usePopoverProps === void 0) {
    usePopoverProps = {};
  }

  var _usePopoverProps = usePopoverProps,
      _usePopoverProps$clos = _usePopoverProps.closeOnBlur,
      closeOnBlur = _usePopoverProps$clos === void 0 ? true : _usePopoverProps$clos,
      _usePopoverProps$clos2 = _usePopoverProps.closeOnEsc,
      closeOnEsc = _usePopoverProps$clos2 === void 0 ? true : _usePopoverProps$clos2,
      initialFocusRef = _usePopoverProps.initialFocusRef,
      id = _usePopoverProps.id,
      _usePopoverProps$retu = _usePopoverProps.returnFocusOnClose,
      returnFocusOnClose = _usePopoverProps$retu === void 0 ? true : _usePopoverProps$retu,
      _usePopoverProps$auto = _usePopoverProps.autoFocus,
      autoFocus = _usePopoverProps$auto === void 0 ? true : _usePopoverProps$auto,
      arrowSize = _usePopoverProps.arrowSize,
      arrowShadowColor = _usePopoverProps.arrowShadowColor,
      _usePopoverProps$trig = _usePopoverProps.trigger,
      trigger = _usePopoverProps$trig === void 0 ? TRIGGER.click : _usePopoverProps$trig,
      _usePopoverProps$open = _usePopoverProps.openDelay,
      openDelay = _usePopoverProps$open === void 0 ? 200 : _usePopoverProps$open,
      _usePopoverProps$clos3 = _usePopoverProps.closeDelay,
      closeDelay = _usePopoverProps$clos3 === void 0 ? 200 : _usePopoverProps$clos3,
      isLazy = _usePopoverProps.isLazy,
      _usePopoverProps$lazy = _usePopoverProps.lazyBehavior,
      lazyBehavior = _usePopoverProps$lazy === void 0 ? 'unmount' : _usePopoverProps$lazy,
      computePositionOnMount = _usePopoverProps.computePositionOnMount,
      popperProps = _objectWithoutPropertiesLoose(_usePopoverProps, ["closeOnBlur", "closeOnEsc", "initialFocusRef", "id", "returnFocusOnClose", "autoFocus", "arrowSize", "arrowShadowColor", "trigger", "openDelay", "closeDelay", "isLazy", "lazyBehavior", "computePositionOnMount"]);

  var _useDisclosure = useDisclosure(usePopoverProps),
      open = _useDisclosure.open,
      onClose = _useDisclosure.onClose,
      onOpen = _useDisclosure.onOpen,
      onToggle = _useDisclosure.onToggle;

  var anchorRef = useRef(null);
  var triggerRef = useRef(null);
  var popoverRef = useRef(null);
  var isHoveringRef = useRef(false);
  var hasBeenOpened = useRef(false);

  if (open) {
    hasBeenOpened.current = true;
  }

  var _useState = useState(false),
      hasHeader = _useState[0],
      setHasHeader = _useState[1];

  var _useState2 = useState(false),
      hasBody = _useState2[0],
      setHasBody = _useState2[1];

  var _useIds = useIds(id, 'popover-trigger', 'popover-content', 'popover-header', 'popover-body'),
      triggerId = _useIds[0],
      popoverId = _useIds[1],
      headerId = _useIds[2],
      bodyId = _useIds[3];

  var _usePopperV = usePopperV2(_extends({}, popperProps, {
    enabled: open || !!computePositionOnMount
  })),
      referenceRef = _usePopperV.referenceRef,
      getArrowProps = _usePopperV.getArrowProps,
      getPopperProps = _usePopperV.getPopperProps,
      getArrowInnerProps = _usePopperV.getArrowInnerProps,
      forceUpdate = _usePopperV.forceUpdate;

  useFocusOnPointerDown({
    enabled: open,
    ref: triggerRef
  });
  useFocusOnHide(popoverRef, {
    focusRef: triggerRef,
    visible: open,
    shouldFocus: returnFocusOnClose && trigger === TRIGGER.click
  });
  useFocusOnShow(popoverRef, {
    focusRef: initialFocusRef,
    visible: open,
    shouldFocus: autoFocus && trigger === TRIGGER.click
  });
  var shouldRenderChildren = determineLazyBehavior({
    hasBeenSelected: hasBeenOpened.current,
    isLazy: isLazy,
    lazyBehavior: lazyBehavior,
    isSelected: open
  });
  var getPopoverProps = useCallback(function (props, _ref) {
    var _extends2;

    if (props === void 0) {
      props = {};
    }

    if (_ref === void 0) {
      _ref = null;
    }

    var popoverProps = _extends({}, props, {
      style: _extends({}, props.style, (_extends2 = {
        transformOrigin: cssVars.transformOrigin.varRef
      }, _extends2[cssVars.arrowSize["var"]] = arrowSize ? px(arrowSize) : undefined, _extends2[cssVars.arrowShadowColor["var"]] = arrowShadowColor, _extends2)),
      ref: mergeRefs(popoverRef, _ref),
      children: shouldRenderChildren ? props.children : null,
      id: popoverId,
      tabIndex: -1,
      role: 'dialog',
      onKeyDown: callAllHandlers(props.onKeyDown, function (event) {
        if (closeOnEsc && event.key === 'Escape') {
          onClose();
        }
      }),
      onBlur: callAllHandlers(props.onBlur, function (event) {
        var relatedTarget = getRelatedTarget(event);
        var targetIsPopover = contains(popoverRef.current, relatedTarget);
        var targetIsTrigger = contains(triggerRef.current, relatedTarget);
        var isValidBlur = !targetIsPopover && !targetIsTrigger;

        if (open && closeOnBlur && isValidBlur) {
          onClose();
        }
      }),
      'aria-labelledby': hasHeader ? headerId : undefined,
      'aria-describedby': hasBody ? bodyId : undefined
    });

    if (trigger === TRIGGER.hover) {
      popoverProps.role = 'tooltip';
      popoverProps.onMouseEnter = callAllHandlers(props.onMouseEnter, function () {
        isHoveringRef.current = true;
      });
      popoverProps.onMouseLeave = callAllHandlers(props.onMouseLeave, function () {
        isHoveringRef.current = false;
        setTimeout(onClose, closeDelay);
      });
    }

    return popoverProps;
  }, [shouldRenderChildren, popoverId, hasHeader, headerId, hasBody, bodyId, trigger, closeOnEsc, onClose, open, closeOnBlur, closeDelay, arrowShadowColor, arrowSize]);
  var getPopoverPositionerProps = useCallback(function (props, forwardedRef) {
    if (props === void 0) {
      props = {};
    }

    if (forwardedRef === void 0) {
      forwardedRef = null;
    }

    return getPopperProps(_extends({}, props, {
      style: _extends({
        visibility: open ? 'visible' : 'hidden'
      }, props.style)
    }), forwardedRef);
  }, [open, getPopperProps]);
  var getAnchorProps = useCallback(function (props, _ref) {
    if (_ref === void 0) {
      _ref = null;
    }

    var anchorProps = _extends({}, props, {
      // If anchor is rendered, it is used as reference.
      ref: mergeRefs(_ref, anchorRef, referenceRef)
    });

    return anchorProps;
  }, [anchorRef, referenceRef]);
  var openTimeout = useRef();
  var closeTimeout = useRef();
  var maybeReferenceRef = useCallback(function (node) {
    // Don't override referenceRef in case the PopoverAnchor is rendered.
    if (anchorRef.current == null) {
      referenceRef(node);
    }
  }, [referenceRef]);
  var getTriggerProps = useCallback(function (props, _ref) {
    if (props === void 0) {
      props = {};
    }

    if (_ref === void 0) {
      _ref = null;
    }

    var triggerProps = _extends({}, props, {
      ref: mergeRefs(triggerRef, _ref, maybeReferenceRef),
      id: triggerId,
      'aria-haspopup': 'dialog',
      'aria-expanded': open,
      'aria-controls': popoverId
    });

    if (trigger === TRIGGER.click) {
      triggerProps.onClick = callAllHandlers(props.onClick, onToggle);
    }

    if (trigger === TRIGGER.hover) {
      /**
       * Any content that shows on pointer hover should also show on keyboard focus.
       * Consider focus and blur to be the `hover` for keyboard users.
       *
       * @see https://www.w3.org/WAI/WCAG21/Understanding/content-on-hover-or-focus.html
       */
      triggerProps.onFocus = callAllHandlers(props.onFocus, onOpen);
      triggerProps.onBlur = callAllHandlers(props.onBlur, function (event) {
        var relatedTarget = getRelatedTarget(event);
        var isValidBlur = !contains(popoverRef.current, relatedTarget);

        if (open && closeOnBlur && isValidBlur) {
          onClose();
        }
      });
      /**
       * Any content that shows on hover or focus must be dismissible.
       * This case pressing `Escape` will dismiss the popover
       */

      triggerProps.onKeyDown = callAllHandlers(props.onKeyDown, function (event) {
        if (event.key === 'Escape') {
          onClose();
        }
      });
      triggerProps.onMouseEnter = callAllHandlers(props.onMouseEnter, function () {
        isHoveringRef.current = true;
        openTimeout.current = window.setTimeout(onOpen, openDelay);
      });
      triggerProps.onMouseLeave = callAllHandlers(props.onMouseLeave, function () {
        isHoveringRef.current = false;

        if (openTimeout.current) {
          clearTimeout(openTimeout.current);
          openTimeout.current = undefined;
        }

        closeTimeout.current = window.setTimeout(function () {
          if (isHoveringRef.current === false) {
            onClose();
          }
        }, closeDelay);
      });
    }

    return triggerProps;
  }, [triggerId, open, popoverId, trigger, maybeReferenceRef, onToggle, onOpen, closeOnBlur, onClose, openDelay, closeDelay]);
  useEffect(function () {
    return function () {
      if (openTimeout.current) {
        clearTimeout(openTimeout.current);
      }

      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
      }
    };
  }, []);
  var getHeaderProps = useCallback(function (props, ref) {
    if (props === void 0) {
      props = {};
    }

    if (ref === void 0) {
      ref = null;
    }

    return _extends({}, props, {
      id: headerId,
      ref: mergeRefs(ref, function (node) {
        setHasHeader(!!node);
      })
    });
  }, [headerId]);
  var getBodyProps = useCallback(function (props, ref) {
    if (props === void 0) {
      props = {};
    }

    if (ref === void 0) {
      ref = null;
    }

    return _extends({}, props, {
      id: bodyId,
      ref: mergeRefs(ref, function (node) {
        setHasBody(!!node);
      })
    });
  }, [bodyId]);
  return {
    forceUpdate: forceUpdate,
    open: open,
    onClose: onClose,
    getAnchorProps: getAnchorProps,
    getArrowProps: getArrowProps,
    getArrowInnerProps: getArrowInnerProps,
    getPopoverPositionerProps: getPopoverPositionerProps,
    getPopoverProps: getPopoverProps,
    getTriggerProps: getTriggerProps,
    getHeaderProps: getHeaderProps,
    getBodyProps: getBodyProps
  };
}

export { usePopover };
//# sourceMappingURL=use-popover.esm.js.map
